import get from 'lodash/get';
import has from 'lodash/has';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';

import Logger from '../tools/Logger';

import { getBFSTraversal, getDomain } from './domainRegistry';

const log = Logger.child({ component: 'domains/externalUtil' });

/**
 * Retrieves values from the domain registry by first looking at the requested domain
 * and then searching through each of its subdomains. In this way root domains surface
 * their subdomains apis.
 *
 * @param {string} domainName - the domain to query
 * @param {string | string[]} keys - a period delimited key path or array of key paths to query
 * @returns {*} value at query path
 */
function getInDomainOrSubdomain(domainName, keys) {
  const domain = getDomain(domainName);

  if (!isObject(domain)) {
    return undefined;
  }

  const foundDomain = getBFSTraversal(domain).find(_domain => {
    return has(_domain.module, keys);
  });
  if (foundDomain) {
    return get(foundDomain.module, keys);
  }

  return undefined;
}

/**
 * Retrieves values from the domain registry
 *
 * @param {string} query - the query string used to select data from the domain registry
 * @param {*} [defaultValue] - the default value to return if queried path is undefined
 * @param {*} [isFunction] - a boolean to know whether or not the default should be returned as a function
 * @returns {*} The domain resource
 * @example
 *    getInDomain('playback.actions.playTrack')
 *    getInDomain('playback')
 */
export function getInDomain(query, defaultValue, isFunction = true) {
  if (!isString(query)) {
    throw new Error('getInDomain(): expected parameter of type String');
  }
  const keys = query.split('.');
  const domainName = keys.shift();
  const domain = get(getDomain(domainName), ['module']);

  if (keys.length === 0 && isObject(domain)) {
    return domain;
  }

  const target = getInDomainOrSubdomain(domainName, keys);

  if (!target && !isUndefined(defaultValue)) {
    return isFunction ? () => defaultValue : defaultValue;
  } else if (!target) {
    throw new Error(`getInDomain(): ${query} does not exist`);
  }

  return target;
}

export function noopWithErrorLog(requestedString) {
  return () => {
    log.error(`noopWithErrorLog(): ${requestedString} does not exist`);
  };
}
